import { api, API_BASE } from '../lib/api';
import { auth, ensureFirebase } from '../lib/firebase';
import type { StashDocument, DocumentFolder } from '../types';

export async function getDocuments(params?: { folderId?: string; ungrouped?: boolean; starred?: boolean }): Promise<StashDocument[]> {
  const q = new URLSearchParams();
  if (params?.folderId) q.set('folderId', params.folderId);
  if (params?.ungrouped) q.set('ungrouped', 'true');
  if (params?.starred) q.set('starred', 'true');
  const qs = q.toString() ? `?${q}` : '';
  return api<StashDocument[]>(`/api/documents${qs}`);
}

export async function getDocument(id: string): Promise<StashDocument> {
  return api<StashDocument>(`/api/documents/${id}`);
}

export async function getDocumentSignedUrl(id: string, disposition: 'inline' | 'attachment' = 'inline'): Promise<string> {
  const { url } = await api<{ url: string }>(`/api/documents/${id}/url?disposition=${disposition}`);
  return url;
}

export async function getDocumentsForItem(itemId: string): Promise<StashDocument[]> {
  return api<StashDocument[]>(`/api/documents?sourceItemId=${encodeURIComponent(itemId)}`);
}

export async function updateDocumentFlags(id: string, flags: number): Promise<void> {
  await api(`/api/documents/${id}/flags`, {
    method: 'PATCH',
    body: JSON.stringify({ flags }),
  });
}

export async function uploadDocument(file: File, folderId?: string | null): Promise<StashDocument> {
  const form = new FormData();
  form.append('file', file);
  if (folderId) form.append('folderId', folderId);
  await ensureFirebase();
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}/api/documents`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateDocument(id: string, updates: { name?: string; folderId?: string | null }): Promise<void> {
  await api(`/api/documents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteDocument(id: string): Promise<void> {
  await api(`/api/documents/${id}`, { method: 'DELETE' });
}

export async function getDocumentFolders(): Promise<DocumentFolder[]> {
  return api<DocumentFolder[]>('/api/document-folders');
}

export async function createDocumentFolder(name: string): Promise<DocumentFolder> {
  return api<DocumentFolder>('/api/document-folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function updateDocumentFolder(id: string, name: string): Promise<void> {
  await api(`/api/document-folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

export async function deleteDocumentFolder(id: string): Promise<void> {
  await api(`/api/document-folders/${id}`, { method: 'DELETE' });
}
